import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";

import { BankList } from "../../api/BankService";
import { getSessions } from "../../api/SessionsService";
import { GetExpenseReport } from "../../api/HostelService";

import HostelHeader from "./HostelHeader";
import Nav from "../master/NavBar";
import Footer from "../main/footer";

import {
  formatDate,
  ToastMain,
  SweetAlertShow,
  LoadingDiv,
} from "../master/Master";

import WelcomeBar from "./WelcomeBar";
import usePrivateLink from "../config/usePrivateLink";
import HostelReportTabs from "./HostelReportTabs";

export default function HostelExpenseReport() {
  const [page, setPage] = useState(1);
  const [Data, setData] = useState(null);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);

  const [selectedSession, setSelectedSessions] = useState("");
  const [sessions, setSessions] = useState([]);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [banks, setBanks] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");

  const [error, setError] = useState("");
  const [transition, setTransition] = useState([]);

  const [hostel_id, setHostelId] = useState(localStorage.getItem("hostel_id"));

  const loadOnce = useRef(false);

  const hasAccess = usePrivateLink();

  /* =========================================================
     DEFAULT DATES
  ========================================================= */

  const getDefaultDates = () => {
    const today = new Date();

    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const formatDefaultDate = (date) => {
      if (!(date instanceof Date)) {
        date = new Date(date);
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    return {
      from: formatDefaultDate(firstDayOfMonth),
      to: formatDefaultDate(today),
    };
  };

  const { from: defaultFrom, to: defaultTo } = getDefaultDates();

  /* =========================================================
     HOSTEL
  ========================================================= */

  useEffect(() => {
    setHostelId(localStorage.getItem("hostel_id"));
  }, []);

  /* =========================================================
     BANK LIST
  ========================================================= */

  const getBusinessBanks = async () => {
    setLoading(true);

    try {
      const response = await BankList();

      if (response?.data?.status) {
        setBanks(response?.data?.data || []);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Something went wrong",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBusinessBanks();
  }, []);

  /* =========================================================
     SESSION
  ========================================================= */

  const getSession = async () => {
    setLoading(true);

    try {
      const response = await getSessions();

      if (response?.data?.status) {
        const sessionList = response?.data?.data || [];

        const activeSession = sessionList.find(
          (session) => session.status === "1",
        );

        if (activeSession) {
          setSelectedSessions(activeSession.id);
        } else if (sessionList.length > 0) {
          setSelectedSessions(sessionList[0].id);
        }

        setSessions(sessionList);
      } else {
        setError("Failed to fetch session.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSession();
  }, []);

  /* =========================================================
     EXPORT
     API FUNCTIONALITY PRESERVED
  ========================================================= */

  const handleExportAll = async () => {
    try {
      setLoading(true);

      let addUrl = `&page=${page}&session_id=${selectedSession}`;

      if (from) addUrl += `&start_date=${from}`;
      if (to) addUrl += `&end_date=${to}`;
      if (selectedAccount) {
        addUrl += `&bank_id=${selectedAccount}`;
      }

      const firstResponse = await GetExpenseReport(`${addUrl}&page=1`);

      let allData = Array.isArray(firstResponse?.data?.data?.data)
        ? firstResponse.data.data.data
        : [];

      const lastPage = firstResponse?.data?.data?.last_page || 1;

      for (let currentPage = 2; currentPage <= lastPage; currentPage++) {
        const response = await GetExpenseReport(
          `${addUrl}&page=${currentPage}`,
        );

        const records = Array.isArray(response?.data?.data?.data)
          ? response.data.data.data
          : [];

        allData.push(...records);
      }

      if (!allData.length) {
        alert("No data to export!");
        setLoading(false);
        return;
      }

      const formattedData = allData.map((item, index) => {
        const expense = item.expense || {};
        const bank = item.bank || {};
        const staff = item.staff || {};

        return {
          "Sr. No": index + 1,
          Date: item.date || "",
          "Expense Title": expense.title || "",
          "Expense Description": expense.description || "",
          "Transaction Description": item.description || "",
          Type: item.type || "",
          "Amount (₹)": item.amount || "",
          "Bank Name": bank.owner_name || "Cash",
          "Bank Account No": bank.account_no || "",
          "Bank Branch": bank.branch || "",
          "Staff Name": staff.name || "N/A",
          "Staff Phone": staff.phone || "",
        };
      });

      const workbook = XLSX.utils.book_new();

      const expenseSheet = XLSX.utils.json_to_sheet(formattedData);

      XLSX.utils.book_append_sheet(
        workbook,
        expenseSheet,
        "Hostel Expense Report",
      );

      const totalSum = firstResponse?.data?.total_sum || 0;

      const totalRow = [
        {
          "Expense Title": "Total Expense (₹)",
          "Amount (₹)": totalSum,
        },
      ];

      XLSX.utils.sheet_add_json(expenseSheet, totalRow, {
        origin: -1,
        skipHeader: true,
      });

      XLSX.writeFile(workbook, "HostelExpenseReport.xlsx");

      ToastMain.fire({
        icon: "success",
        title: `Exported ${allData.length} expense records successfully!`,
      });
    } catch (error) {
      console.error(error);

      SweetAlertShow("error", "Error", error.message);
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     FETCH REPORT
     API FUNCTIONALITY PRESERVED
  ========================================================= */

  const fetchData = async () => {
    setLoading(true);

    let addUrl = `&page=${page}&session_id=${selectedSession}`;

    if (from) addUrl += `&start_date=${from}`;
    if (to) addUrl += `&end_date=${to}`;

    if (selectedAccount) {
      addUrl += `&bank_id=${selectedAccount}`;
    }

    try {
      const response = await GetExpenseReport(addUrl);

      setData(response?.data?.data);
      setTransition(response?.data?.data || []);
      setTotal(response?.data?.total_sum);
    } catch (error) {
      console.error("Failed to fetch expense data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedSession) return;

    fetchData();
  }, [page, from, to, selectedSession, selectedAccount]);

  /* =========================================================
     HELPERS
  ========================================================= */

  const formatAmount = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getCurrentSessionName = () => {
    const session = sessions?.find(
      (item) => String(item?.id) === String(selectedSession),
    );

    return session?.session || "-";
  };

  const getSelectedBankName = () => {
    if (!selectedAccount) {
      return "All Accounts";
    }

    const bank = banks?.find(
      (item) => String(item?.id) === String(selectedAccount),
    );

    return bank
      ? `${bank?.account_no || ""} (${bank?.owner_name || ""})`
      : "Selected Account";
  };

  const resetFilters = () => {
    setFrom("");
    setTo("");
    setSelectedAccount("");
    setPage(1);
  };

  const hasActiveFilters = from || to || selectedAccount;

  return (
    <>
      {/* =========================================================
          LOADING
      ========================================================= */}

      {loading && <LoadingDiv />}

      {/* =========================================================
          MAIN LAYOUT
      ========================================================= */}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <HostelHeader />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                {/* <WelcomeBar /> */}

                {/* =================================================
                    PAGE HEADER
                ================================================= */}

                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-3 bg-label-danger d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 48,
                        height: 48,
                      }}
                    >
                      <i className="ri-wallet-3-line fs-4 text-danger" />
                    </div>

                    <div>
                      <h4 className="mb-1 fw-bold">Expense Report</h4>

                      <p className="mb-0 text-muted">
                        Review and track hostel expense transactions
                      </p>
                    </div>
                  </div>

                  {/* <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-label-primary px-3 py-2">
                      <i className="ri-calendar-check-line me-1" />
                      {getCurrentSessionName()}
                    </span>
                  </div> */}
                </div>

                {/* =================================================
                    REPORT NAVIGATION
                ================================================= */}

                {/* <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body p-2">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {hasAccess("/hostel/reports") && (
                        <Link
                          to="/hostel/reports"
                          className="btn btn-sm btn-label-primary"
                        >
                          <i className="ri-dashboard-line me-1" />
                          Overview
                        </Link>
                      )}

                      {hasAccess("/hostel/credit/debit/report") && (
                        <Link
                          to="/hostel/credit/debit/report"
                          className="btn btn-sm btn-label-primary"
                        >
                          <i className="ri-exchange-dollar-line me-1" />
                          Credit Debit Report
                        </Link>
                      )}

                      {hasAccess("/hostel/fee/report") && (
                        <Link
                          to="/hostel/fee/report"
                          className="btn btn-sm btn-primary"
                        >
                          <i className="ri-money-rupee-circle-line me-1" />
                          Fee Report
                        </Link>
                      )}

                      {hasAccess("/hostel/expense/report") && (
                        <Link
                          to="/hostel/expense/report"
                          className="btn btn-sm btn-label-primary"
                        >
                          <i className="ri-wallet-3-line me-1" />
                          Expense Report
                        </Link>
                      )}

                      {hasAccess("/hostel/fee/due/report") && (
                        <Link
                          to="/hostel/fee/due/report"
                          className="btn btn-sm btn-label-primary"
                        >
                          <i className="ri-error-warning-line me-1" />
                          Due Fee Report
                        </Link>
                      )}

                      {hasAccess("/hostel/student/month/report") && (
                        <Link
                          to="/hostel/student/month/report"
                          className="btn btn-sm btn-label-primary"
                        >
                          <i className="ri-calendar-line me-1" />
                          Hostel Student Report
                        </Link>
                      )}


                      <div className="ms-lg-auto">
                        <div
                          className="input-group input-group-sm"
                          style={{
                            minWidth: 220,
                          }}
                        >
                          <span className="input-group-text">
                            <i className="ri-calendar-check-line" />
                          </span>

                          <select
                            className="form-select"
                            value={selectedSession}
                            onChange={(e) => {
                              setSelectedSessions(e.target.value);
                              setPage(1);
                            }}
                          >
                            {sessions?.map((session) => (
                              <option key={session?.id} value={session?.id}>
                                {session?.session}
                                {" - "}
                                {session?.status == "1" ? "Active" : "Inactive"}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}
                <HostelReportTabs />

                {/* =================================================
                    FILTER CARD
                ================================================= */}

                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header border-bottom py-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="rounded-2 bg-label-primary d-flex align-items-center justify-content-center"
                          style={{
                            width: 38,
                            height: 38,
                          }}
                        >
                          <i className="ri-filter-3-line text-primary fs-5" />
                        </div>

                        <div>
                          <h5 className="mb-0">Filter Report</h5>

                          <small className="text-muted">
                            Refine expenses by date and account
                          </small>
                        </div>
                      </div>

                      {hasActiveFilters && (
                        <button
                          type="button"
                          className="btn btn-sm btn-text-secondary"
                          onClick={resetFilters}
                        >
                          <i className="ri-refresh-line me-1" />
                          Clear Filters
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="row g-3">
                      {/* FROM DATE */}

                      <div className="col-md-4 col-xl-3">
                        <label className="form-label fw-semibold">
                          From Date
                        </label>

                        <div className="input-group input-group-merge">
                          <span className="input-group-text">
                            <i className="ri-calendar-line" />
                          </span>

                          <input
                            type="date"
                            className="form-control"
                            value={from}
                            onChange={(e) => {
                              setFrom(e.target.value);
                              setPage(1);
                            }}
                          />
                        </div>
                      </div>

                      {/* TO DATE */}

                      <div className="col-md-4 col-xl-3">
                        <label className="form-label fw-semibold">
                          To Date
                        </label>

                        <div className="input-group input-group-merge">
                          <span className="input-group-text">
                            <i className="ri-calendar-check-line" />
                          </span>

                          <input
                            type="date"
                            className="form-control"
                            value={to}
                            min={from || undefined}
                            onChange={(e) => {
                              setTo(e.target.value);
                              setPage(1);
                            }}
                          />
                        </div>
                      </div>

                      {/* BANK */}

                      <div className="col-md-4 col-xl-4">
                        <label className="form-label fw-semibold">
                          Account
                        </label>

                        <div className="input-group input-group-merge">
                          <span className="input-group-text">
                            <i className="ri-bank-line" />
                          </span>

                          <select
                            name="bank_id"
                            className="form-select"
                            value={selectedAccount}
                            onChange={(e) => {
                              setSelectedAccount(e.target.value);
                              setPage(1);
                            }}
                          >
                            <option value="">All Accounts</option>

                            {banks?.map((item) => (
                              <option key={item?.id} value={item?.id}>
                                {item?.account_no}
                                {" - "}
                                {item?.owner_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* FILTER STATUS */}

                      <div className="col-xl-2">
                        <label className="form-label fw-semibold">
                          Filter Status
                        </label>

                        <div
                          className="rounded-3 bg-label-primary d-flex align-items-center px-3"
                          style={{
                            minHeight: 44,
                          }}
                        >
                          <i className="ri-filter-line text-primary me-2" />

                          <span className="text-primary fw-semibold text-truncate">
                            {hasActiveFilters ? "Filtered" : "All Expenses"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* ACTIVE FILTERS */}

                    {hasActiveFilters && (
                      <div className="mt-3 pt-3 border-top">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <small className="text-muted fw-semibold me-1">
                            Active:
                          </small>

                          {from && (
                            <span className="badge bg-label-primary">
                              <i className="ri-calendar-line me-1" />
                              From: {from}
                            </span>
                          )}

                          {to && (
                            <span className="badge bg-label-primary">
                              <i className="ri-calendar-check-line me-1" />
                              To: {to}
                            </span>
                          )}

                          {selectedAccount && (
                            <span className="badge bg-label-info">
                              <i className="ri-bank-line me-1" />
                              {getSelectedBankName()}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* =================================================
                    SUMMARY CARDS
                ================================================= */}

                <div className="row g-3 mb-4">
                  {/* TOTAL EXPENSE */}

                  <div className="col-md-6 col-xl-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">Total Expense</small>

                            <h4 className="mb-0 mt-1 fw-bold">
                              ₹{formatAmount(total)}
                            </h4>
                          </div>

                          <div className="rounded-3 bg-label-danger p-3">
                            <i className="ri-wallet-3-line fs-4 text-danger" />
                          </div>
                        </div>

                        <div className="mt-3">
                          <small className="text-muted">
                            Total expense amount for the selected filters
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* RECORD COUNT */}

                  <div className="col-md-6 col-xl-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">
                              Expense Records
                            </small>

                            <h4 className="mb-0 mt-1 fw-bold">
                              {Data?.total || transition?.length || 0}
                            </h4>
                          </div>

                          <div className="rounded-3 bg-label-primary p-3">
                            <i className="ri-file-list-3-line fs-4 text-primary" />
                          </div>
                        </div>

                        <div className="mt-3">
                          <small className="text-muted">
                            Transactions matching current filters
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* EXPORT */}

                  <div className="col-md-12 col-xl-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">Report Export</small>

                            <h6 className="mb-1 mt-1 fw-bold">
                              Download Excel Report
                            </h6>

                            <small className="text-muted">
                              Export all matching expense records
                            </small>
                          </div>

                          {hasAccess("/hostel/credit/debit/report/export") && (
                            <button
                              type="button"
                              className="btn btn-success d-flex align-items-center gap-2"
                              onClick={handleExportAll}
                              title="Export Excel"
                            >
                              <i className="ri-file-excel-2-line fs-5" />
                              <span>Export</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    EXPENSE TABLE
                ================================================= */}

                <div className="card border-0 shadow-sm">
                  {/* TABLE HEADER */}

                  <div className="card-header border-bottom py-3">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <h5 className="mb-0 fw-bold">Expense Transactions</h5>

                          <span className="badge bg-label-danger">
                            Expenses
                          </span>
                        </div>

                        <small className="text-muted">
                          Detailed hostel expense transaction history
                        </small>
                      </div>

                      <div className="text-muted small">
                        Session: <strong>{getCurrentSessionName()}</strong>
                      </div>
                    </div>
                  </div>

                  {/* TABLE */}

                  <div className="card-body p-0">
                    {Data?.data?.length > 0 ? (
                      <div
                        className="table-responsive"
                        style={{
                          maxHeight: "65vh",
                          overflow: "auto",
                        }}
                      >
                        <table className="table table-hover align-middle mb-0">
                          <thead
                            className="table-light"
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 5,
                            }}
                          >
                            <tr className="text-center">
                              <th
                                style={{
                                  minWidth: 60,
                                }}
                              >
                                #
                              </th>

                              <th
                                style={{
                                  minWidth: 130,
                                }}
                              >
                                Date
                              </th>

                              <th
                                className="text-start"
                                style={{
                                  minWidth: 240,
                                }}
                              >
                                Expense
                              </th>

                              <th
                                style={{
                                  minWidth: 240,
                                }}
                              >
                                Bank / Account
                              </th>

                              <th
                                style={{
                                  minWidth: 150,
                                }}
                              >
                                Amount
                              </th>

                              <th
                                className="text-start"
                                style={{
                                  minWidth: 280,
                                }}
                              >
                                Description
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {Data?.data?.map((data, i) => {
                              const expense = data?.expense || {};

                              const bank = data?.bank || {};

                              const staff = data?.staff || {};

                              return (
                                <tr key={data?.id || i}>
                                  {/* INDEX */}

                                  <td className="text-center">
                                    <span
                                      className="d-inline-flex align-items-center justify-content-center rounded-circle bg-label-secondary fw-semibold"
                                      style={{
                                        width: 30,
                                        height: 30,
                                      }}
                                    >
                                      {i + 1}
                                    </span>
                                  </td>

                                  {/* DATE */}

                                  <td className="text-center">
                                    <div className="d-flex flex-column align-items-center">
                                      <span className="fw-semibold">
                                        {formatDate(data?.date)}
                                      </span>
                                    </div>
                                  </td>

                                  {/* EXPENSE */}

                                  <td>
                                    <div className="d-flex align-items-center gap-3">
                                      <div
                                        className="rounded-3 bg-label-danger d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{
                                          width: 40,
                                          height: 40,
                                        }}
                                      >
                                        <i className="ri-wallet-3-line text-danger fs-5" />
                                      </div>

                                      <div>
                                        <div className="fw-semibold">
                                          {expense?.title || "Expense"}
                                        </div>

                                        {expense?.description && (
                                          <small
                                            className="text-muted d-block text-truncate"
                                            style={{
                                              maxWidth: 200,
                                            }}
                                          >
                                            {expense.description}
                                          </small>
                                        )}
                                      </div>
                                    </div>
                                  </td>

                                  {/* BANK */}

                                  <td className="text-center">
                                    {bank?.account_no ? (
                                      <div>
                                        <div className="d-flex align-items-center justify-content-center gap-1 fw-semibold">
                                          <i className="ri-bank-line text-primary" />

                                          <span>{bank.account_no}</span>
                                        </div>

                                        <small className="text-muted">
                                          {bank?.owner_name || "Account"}
                                        </small>
                                      </div>
                                    ) : (
                                      <span className="badge bg-label-secondary px-3 py-2">
                                        <i className="ri-money-cash-line me-1" />
                                        Cash
                                      </span>
                                    )}
                                  </td>

                                  {/* AMOUNT */}

                                  <td className="text-end">
                                    <span className="badge bg-label-danger px-3 py-2 fw-bold">
                                      ₹{formatAmount(data?.amount)}
                                    </span>
                                  </td>

                                  {/* DESCRIPTION */}

                                  <td>
                                    <div>
                                      <div className="fw-medium">
                                        {data?.description ||
                                          "No transaction description"}
                                      </div>

                                      {staff?.name && (
                                        <small className="text-muted d-flex align-items-center gap-1 mt-1">
                                          <i className="ri-user-line" />

                                          {staff.name}

                                          {staff?.phone && (
                                            <>
                                              <span>•</span>

                                              {staff.phone}
                                            </>
                                          )}
                                        </small>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      /* =================================================
                         EMPTY STATE
                      ================================================= */

                      <div className="text-center py-5 px-3">
                        <div
                          className="rounded-circle bg-label-secondary d-flex align-items-center justify-content-center mx-auto mb-3"
                          style={{
                            width: 72,
                            height: 72,
                          }}
                        >
                          <i className="ri-file-search-line fs-2 text-muted" />
                        </div>

                        <h5 className="fw-bold mb-2">No Expense Records</h5>

                        <p className="text-muted mb-3">
                          No expense transactions were found for the selected
                          filters.
                        </p>

                        {hasActiveFilters && (
                          <button
                            type="button"
                            className="btn btn-sm btn-label-primary"
                            onClick={resetFilters}
                          >
                            <i className="ri-refresh-line me-1" />
                            Clear Filters
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* =================================================
                      PAGINATION
                  ================================================= */}

                  {Data?.links?.length > 0 && (
                    <div className="card-footer border-top">
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                        <div>
                          <small className="text-muted">
                            Showing <strong>{Data?.from || 0}</strong> to{" "}
                            <strong>{Data?.to || 0}</strong> of{" "}
                            <strong>{Data?.total || 0}</strong> entries
                          </small>
                        </div>

                        <nav aria-label="Page navigation">
                          <ul className="pagination pagination-sm mb-0">
                            {Data?.links?.map((btn, i) => (
                              <li
                                key={`${btn?.label}-${i}`}
                                className={`page-item ${
                                  btn?.active ? "active" : ""
                                } ${btn?.url ? "" : "disabled"}`}
                              >
                                <button
                                  type="button"
                                  className="page-link waves-effect"
                                  onClick={() => {
                                    if (Data?.links?.length === i + 1) {
                                      btn?.url &&
                                        setPage(Data?.current_page + 1);
                                    } else if (i === 0) {
                                      btn?.url &&
                                        setPage(Data?.current_page - 1);
                                    } else {
                                      setPage(Number(btn?.label));
                                    }
                                  }}
                                >
                                  {Data?.links?.length === i + 1 ? (
                                    <i className="ri-arrow-right-line" />
                                  ) : i === 0 ? (
                                    <i className="ri-arrow-left-line" />
                                  ) : (
                                    btn?.label
                                  )}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Footer />

              <div className="content-backdrop fade"></div>
            </div>
          </div>
        </div>

        <div className="layout-overlay layout-menu-toggle"></div>
        <div className="drag-target"></div>
      </div>
    </>
  );
}
